<template>
  <div v-bind:id="id" class="member-user-menu-item-additional-item-selector">
    <a class="item" v-on:click="isMenuOpen = !isMenuOpen">
      {{ $t('appMemberUserMenuItemAdditionalItemSelector.displaySetting') }}
      <icon-arrow-down
        class="icon-arrow"
        v-bind:class="{ reverse: isMenuOpen }"
      />
    </a>
    <div v-show="isMenuOpen">
      <b-form-radio-group
        v-bind:id="elementId.radio"
        stacked
        varient="success"
        v-bind:checked="isSimple"
        class="radio-form"
        v-on:change="$_emitIsSimpleChangedEvent"
      >
        <label class="label">
          <b-form-radio class="radio" v-bind:value="true" />
          <span
            class="option-text"
            v-t.preserve="
              'appMemberUserMenuItemAdditionalItemSelector.option.simple'
            "
          />
        </label>
        <label class="label">
          <b-form-radio class="radio detail" v-bind:value="false" />
          <span
            class="option-text"
            v-t.preserve="
              'appMemberUserMenuItemAdditionalItemSelector.option.detailed'
            "
          />
          <a
            class="select-item-link"
            v-on:click="$_showModal"
            v-t.preserve="
              'appMemberUserMenuItemAdditionalItemSelector.message.selectDisplayItems'
            "
          />
        </label>
      </b-form-radio-group>
    </div>
    <!-- 項目選択モーダル -->
    <ifield-b-modal
      v-bind:id="elementId.modal"
      ref="modal"
      centered
      scrollable
      ok-variant="success"
      v-bind:title="
        $t(
          'appMemberUserMenuItemAdditionalItemSelector.title.selectDisplayItems'
        )
      "
      v-bind:ok-title="
        $t('appMemberUserMenuItemAdditionalItemSelector.action.save')
      "
      v-bind:cancel-title="
        $t('appMemberUserMenuItemAdditionalItemSelector.action.cancel')
      "
      body-class="selector-modal-body"
      v-on:ok="$_emitItemsChangedEvent"
      v-on:show="$emit('show-item-selector')"
      v-on:hide="$emit('hide-item-selector')"
    >
      <item-selector
        multi
        v-bind:titles="selectorTitles"
        v-bind:data="selectorOptions"
        v-bind:value-field="'item'"
        v-model="currentItems"
      />
    </ifield-b-modal>
  </div>
</template>

<script>
import IconArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import {
  ITEM_TYPE,
  useUserStatusAdditionalItems,
} from '@/composables/member/UseUserStatusAdditionalItems'
import { FUNCTION_TYPE } from '@/constants'
import { mapGetters } from 'vuex'

export default {
  name: 'AppMemberUserMenuItemAdditionalItemSelector',
  components: {
    IconArrowDown,
  },
  props: {
    /**
     * ルート要素のID
     * 子要素のIDのprefixとしても使用
     */
    id: {
      type: String,
      default: 'user-menu-item-additional-item-selector',
    },
    /**
     * 項目値
     */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * 簡易表示フラグ
     */
    isSimple: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { handleApiError } = useHandleApiError()
    const { selectableItems } = useUserStatusAdditionalItems()
    return { handleApiError, selectableItems }
  },
  data: function () {
    return {
      // 選択している項目
      currentItems: [],
      // 項目選択モーダルの表示フラグ
      isItemSelectorShow: false,
      // 表示切り替えメニューの開閉フラグ
      isMenuOpen: false,
    }
  },
  computed: {
    ...mapGetters('userAttribute', ['userAttributeMap']),
    ...mapGetters('sensorItem', ['sensorItemMap']),
    /**
     * 要素ID
     * @type {Object}
     */
    elementId: function () {
      return {
        radio: `${this.id}-radio`,
        modal: `${this.id}-modal`,
      }
    },
    /**
     * チェックインの利用権限がある場合はtrue
     * @type {Boolean}
     */
    isCheckinAvaliable: function () {
      return !!this.$store.getters['functionType'](FUNCTION_TYPE.CHECKIN)
    },
    /**
     * 項目種類配列
     * @type {Array.<string>}
     */
    itemTypes: function () {
      return [...Object.values(ITEM_TYPE)]
    },
    /**
     * ItemSelector用のタイトル定義
     * @type {Array.<Object>}
     */
    selectorTitles: function () {
      return [
        {
          key: 'name',
          label: this.$t(
            'appMemberUserMenuItemAdditionalItemSelector.itemType'
          ),
        },
      ]
    },
    /**
     * ItemSelector用の選択肢データ
     * @type {Array.<Object>}
     */
    selectorOptions: function () {
      return this.selectableItems.map((item) => {
        const [type, targetId] = item.split(':')
        return {
          item,
          name: this.$_getOptionLabel(type, targetId),
        }
      })
    },
  },
  watch: {
    items: {
      immediate: true,
      handler: function (value) {
        this.currentItems = [...value]
      },
    },
  },
  created: function () {
    this.$_getSensorItems()
    this.$_getUserAttributes()
  },
  methods: {
    /**
     * センサー項目データ取得
     */
    $_getSensorItems: async function () {
      try {
        await this.$store.dispatch('sensorItem/updateSensorItemsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch sensorItems. use fetched before.')
        )
      }
    },

    /**
     * ユーザー属性データ取得
     */
    $_getUserAttributes: async function () {
      try {
        await this.$store.dispatch(
          'userAttribute/updateUserAttributesWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch userAttributes. use fetched before.')
        )
      }
    },

    /**
     * 項目選択肢の表示名を取得する
     * @param {string} itemType 項目種類
     * @param {string} targetId 対象ID(センサー,ユーザー属性時のみ使用)
     * @returns {string} 表示名 取得出来ない時は空文字
     */
    $_getOptionLabel: function (itemType, targetId) {
      if (itemType === ITEM_TYPE.SENSOR_DATA) {
        // センサー測定項目名称に変換
        const sensorItem = this.sensorItemMap.get(+targetId)
        return sensorItem?.sensor_item_name || ''
      } else if (itemType === ITEM_TYPE.ATTRIBUTE) {
        // ユーザー属性名称に変換
        const userAttribute = this.userAttributeMap.get(+targetId)
        return userAttribute?.attribute_name || ''
      } else if (itemType === ITEM_TYPE.PLACE) {
        return this.isCheckinAvaliable
          ? this.$t('appMemberUserMenuItemAdditionalItemSelector.item.checkin')
          : this.$t('appMemberUserMenuItemAdditionalItemSelector.item.place')
      } else if (itemType === ITEM_TYPE.POSITIONING) {
        return this.$t(
          'appMemberUserMenuItemAdditionalItemSelector.item.positioningDate'
        )
      } else if (itemType === ITEM_TYPE.REGISTRATION) {
        return this.$t(
          'appMemberUserMenuItemAdditionalItemSelector.item.registrationDate'
        )
      } else if (itemType === ITEM_TYPE.ACCURACY_RATING) {
        return this.$t(
          'appMemberUserMenuItemAdditionalItemSelector.item.accuracyRating'
        )
      } else if (itemType === ITEM_TYPE.SENSOR_NAMES) {
        // i18n のキーは lowerCamelCase にするため item_type をそのまま使わない
        return this.$t(
          'appMemberUserMenuItemAdditionalItemSelector.item.sensorNames'
        )
      } else if (this.itemTypes.includes(itemType)) {
        return this.$t(
          `appMemberUserMenuItemAdditionalItemSelector.item.${itemType}`
        )
      } else {
        return ''
      }
    },

    /**
     * 項目選択モーダル表示
     */
    $_showModal: function () {
      this.$refs['modal'].show()
    },

    /**
     * 簡易表示フラグ変更イベント発行
     * @param {Boolean} value 変更値
     */
    $_emitIsSimpleChangedEvent: function (value) {
      /**
       * 簡易表示フラグ変更イベント
       * @param {Boolean} isSimple 変更値
       */
      this.$emit('is-simple-changed', value)
    },

    /**
     * 項目選択変更イベント発行
     */
    $_emitItemsChangedEvent: function () {
      /**
       * 項目選択変更イベント
       * @param {Object} value 変更値
       */
      this.$emit('items-changed', [...this.currentItems])
    },
  },
}
</script>

<style lang="scss" scoped>
.radio-form {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 2px;
  padding: 0 16px 10px 16px;

  .label {
    font-size: $font-size-middle;
    white-space: nowrap;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;

    &:hover {
      cursor: pointer;
    }

    ::v-deep .custom-control {
      // テキストやフォームをラジオボタンに少し近づけるため右側 margin を調整
      margin: 0 -2px 0 0;
    }

    ::v-deep .custom-control-input {
      // 選択時のスタイル => 色を緑系にする
      &:checked ~ .custom-control-label::before {
        border-color: $entity-color-green;
        background-color: $entity-color-green;
      }

      // focus時のshadowは出さない
      &:focus ~ .custom-control-label::before {
        box-shadow: none;
      }

      // afterが一番上に重なるのでafterにcursor指定する
      &:hover ~ .custom-control-label::after {
        cursor: pointer;
      }
    }

    .select-item-link {
      color: $text-color-link;
      margin-left: 8px;
    }
  }
}

::v-deep .selector-modal-body {
  background-color: #ffffff;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 30px;
  padding: 8px 16px;
  color: $text-color;
  font-size: $font-size-middle;
  line-height: 1;
  text-decoration: none;
  background-color: #ffffff;
  transition: 0.2s;

  &:hover {
    background-color: #f6f6f6;
    cursor: pointer;
  }
}
.icon-arrow {
  fill: none;
  stroke: $text-color-weak;
  stroke-width: 1.5px;
  margin: -8px -4px;
  transition: transform 0.3s;
  &.reverse {
    transform: rotate(180deg);
  }
}
</style>
